import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ETIQUETA_GENETICA, creditos, fecha, fechaHora, formatearCantidad, sufijoUnidad } from "../lib/formato";
import { Cabecera, Cargando, Etiqueta, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Lote {
  id: string;
  codigo: string;
  cantidadInicialMg: number;
  cantidadActualMg: number;
  costeCentimos: number;
  fechaEntrada: string;
  caducidad: string | null;
  proveedor: string | null;
  notas: string | null;
}

interface Movimiento {
  id: string;
  fecha: string;
  tipo: string;
  cantidadMg: number;
  stockPrevio: number;
  stockNuevo: number;
  motivo: string | null;
  usuario: { nombre: string } | null;
  loteId: string | null;
}

interface Producto {
  id: string;
  nombre: string;
  descripcion: string | null;
  genetica: string;
  thc: number | null;
  cbd: number | null;
  precioCentimos: number;
  stockMg: number;
  stockMinMg: number;
  activo: boolean;
  categoria: { nombre: string; color: string; unidadMedida: string };
  lotes: Lote[];
  movimientos: Movimiento[];
}

/** Trazabilidad de una variedad: de que lotes viene el stock y por donde ha pasado. */
export default function ProductoDetalle() {
  const { t } = useIdioma();
  const { id = "" } = useParams();
  const { puede } = useSesion();
  const producto = useQuery({ queryKey: ["producto", id], queryFn: () => api<Producto>(`/catalogo/productos/${id}`) });

  if (producto.isLoading || !producto.data) return <Cargando />;
  const p = producto.data;
  const unidad = p.categoria.unidadMedida;
  const ahora = Date.now();
  const lotesPorCodigo = new Map(p.lotes.map((l) => [l.id, l.codigo]));

  const costeMedio = (() => {
    const conCoste = p.lotes.filter((l) => l.costeCentimos > 0 && l.cantidadInicialMg > 0);
    if (conCoste.length === 0) return null;
    const total = conCoste.reduce((s, l) => s + l.costeCentimos, 0);
    const cantidad = conCoste.reduce((s, l) => s + l.cantidadInicialMg, 0);
    return Math.round((total * 1000) / cantidad);
  })();

  return (
    <>
      <Cabecera titulo={p.nombre} descripcion={`${p.categoria.nombre} · ${ETIQUETA_GENETICA[p.genetica] ?? p.genetica}`}>
        <Link className="boton secundario" to="/catalogo">
          {t("pd.volver")}
        </Link>
        {puede("gestionarInventario") && (
          <Link className="boton" to="/inventario">
            {t("pd.entradasYMermas")}
          </Link>
        )}
      </Cabecera>

      <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("pd.stock")}
          valor={formatearCantidad(p.stockMg, unidad)}
          color={p.stockMinMg > 0 && p.stockMg <= p.stockMinMg ? "var(--rojo)" : undefined}
          pie={
            p.stockMinMg > 0
              ? t("pd.minimo", { cantidad: formatearCantidad(p.stockMinMg, unidad) })
              : t("pd.sinMinimo")
          }
        />
        <TarjetaDato
          etiqueta={t("pd.aportacion")}
          valor={`${creditos(p.precioCentimos)}/${sufijoUnidad(unidad)}`}
        />
        <TarjetaDato
          etiqueta={t("pd.costeMedio")}
          valor={costeMedio === null ? "—" : `${creditos(costeMedio)}/${sufijoUnidad(unidad)}`}
          pie={costeMedio === null ? t("pd.sinCoste") : t("pd.segunLotes")}
        />
        <TarjetaDato
          etiqueta={t("pd.thcCbd")}
          valor={`${p.thc != null ? `${p.thc}%` : "—"} / ${p.cbd != null ? `${p.cbd}%` : "—"}`}
          pie={p.activo ? t("pd.disponible") : t("pd.retirado")}
        />
      </div>

      {p.descripcion && (
        <Panel>
          <p style={{ margin: 0 }}>{p.descripcion}</p>
        </Panel>
      )}

      <div className="columna" style={{ gap: 16, marginTop: p.descripcion ? 16 : 0 }}>
        <Panel titulo={t("pd.lotes")} sinCuerpo>
          {p.lotes.length === 0 ? (
            <Vacio>{t("pd.sinLotes")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("pd.lote")}</th>
                    <th>{t("pd.entrada")}</th>
                    <th>{t("pd.origen")}</th>
                    <th className="num">{t("pd.inicial")}</th>
                    <th className="num">{t("pd.queda")}</th>
                    <th className="num">{t("pd.coste")}</th>
                    <th>{t("pd.caducidad")}</th>
                    <th>{t("pd.estado")}</th>
                  </tr>
                </thead>
                <tbody>
                  {p.lotes.map((l) => {
                    const agotado = l.cantidadActualMg <= 0;
                    const caducado = l.caducidad ? new Date(l.caducidad).getTime() < ahora : false;
                    const consumido = l.cantidadInicialMg > 0 ? Math.round(((l.cantidadInicialMg - l.cantidadActualMg) / l.cantidadInicialMg) * 100) : 0;
                    return (
                      <tr key={l.id} style={agotado ? { opacity: 0.55 } : undefined}>
                        <td>
                          <strong className="tabular">{l.codigo}</strong>
                          {l.notas && <div className="suave pequeno">{l.notas}</div>}
                        </td>
                        <td className="pequeno">{fecha(l.fechaEntrada)}</td>
                        <td className="pequeno">{l.proveedor ?? <span className="suave">—</span>}</td>
                        <td className="num tabular">{formatearCantidad(l.cantidadInicialMg, unidad)}</td>
                        <td className="num tabular">
                          {formatearCantidad(l.cantidadActualMg, unidad)}
                          <div className="suave pequeno">{t("pd.consumido", { n: consumido })}</div>
                        </td>
                        <td className="num tabular">{l.costeCentimos > 0 ? creditos(l.costeCentimos) : "—"}</td>
                        <td className="pequeno">{l.caducidad ? fecha(l.caducidad) : <span className="suave">—</span>}</td>
                        <td>
                          {agotado ? (
                            <Etiqueta>{t("pd.agotado")}</Etiqueta>
                          ) : caducado ? (
                            <Etiqueta tipo="roja">{t("pd.caducado")}</Etiqueta>
                          ) : (
                            <Etiqueta tipo="verde">{t("pd.enUso")}</Etiqueta>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel titulo={t("pd.ultimosMovimientos")} sinCuerpo>
          {p.movimientos.length === 0 ? (
            <Vacio>{t("pd.sinMovimientos")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("pd.fecha")}</th>
                    <th>{t("pd.tipo")}</th>
                    <th>{t("pd.detalle")}</th>
                    <th>{t("pd.lote")}</th>
                    <th className="num">{t("pd.cantidad")}</th>
                    <th className="num">{t("pd.stockDespues")}</th>
                    <th>{t("pd.usuario")}</th>
                  </tr>
                </thead>
                <tbody>
                  {p.movimientos.map((m) => (
                    <tr key={m.id}>
                      <td className="pequeno suave">{fechaHora(m.fecha)}</td>
                      <td>
                        <Etiqueta tipo={m.cantidadMg >= 0 ? "verde" : m.tipo === "MERMA" ? "roja" : ""}>
                          {t(`movimiento.${m.tipo}` as Clave)}
                        </Etiqueta>
                      </td>
                      <td className="pequeno">{m.motivo ?? <span className="suave">—</span>}</td>
                      <td className="pequeno tabular">{m.loteId ? (lotesPorCodigo.get(m.loteId) ?? "—") : <span className="suave">—</span>}</td>
                      <td className="num tabular" style={{ color: m.cantidadMg >= 0 ? "var(--verde)" : "var(--rojo)" }}>
                        {m.cantidadMg >= 0 ? "+" : "−"}
                        {formatearCantidad(Math.abs(m.cantidadMg), unidad)}
                      </td>
                      <td className="num tabular">{formatearCantidad(m.stockNuevo, unidad)}</td>
                      <td className="pequeno suave">{m.usuario?.nombre ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
